import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Kanban, WarningCircle, ListChecks } from '@phosphor-icons/react';
import { useT } from '@/i18n/LanguageContext';
import { plannerApi } from './plannerApi';
import { PRIORITY_BAR, PRIORITY_BADGE, PROJECT_STATUS_BADGE, projectBadgeKey, fmtDateFull, ganttOrder, makeNameShortener } from './plannerConstants';
import GanttChart from './components/GanttChart';
import { Badge } from '@/components/ui/badge';

const oneOrRange = (s, e) => {
  if (!s && !e) return '—';
  if (!s) return fmtDateFull(e);
  if (!e) return fmtDateFull(s);
  return String(s).slice(0, 10) === String(e).slice(0, 10) ? fmtDateFull(s) : `${fmtDateFull(s)}〜${fmtDateFull(e)}`;
};

// 公開ガントページ — /g/{token}。ログイン不要・読み取り専用。
export default function PublicGantt() {
  const { token } = useParams();
  const { t } = useT();
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  const [needPw, setNeedPw] = useState(false);
  const [password, setPassword] = useState('');
  const [pwError, setPwError] = useState('');

  const load = (session) => plannerApi.sharedGantt(token, session)
    .then((d) => { setData(d); setNeedPw(false); })
    .catch((e) => {
      if (e?.response?.status === 401) setNeedPw(true);
      else setErr(e?.response?.data?.detail || '共有リンクが無効です');
    });

  useEffect(() => { load(); }, [token]);   // eslint-disable-line react-hooks/exhaustive-deps

  const unlock = async (e) => {
    e?.preventDefault?.();
    setPwError('');
    try {
      const r = await plannerApi.unlockShare(token, password);
      await load(r.session);
    } catch (ex) {
      setPwError(ex?.response?.status === 423
        ? '間違いが続いたため、しばらく開けません（15分）'
        : 'パスワードが違います');
    }
  };

  if (needPw) {
    return (
      <div className="min-h-screen bg-zinc-50 flex items-center justify-center p-6">
        <form onSubmit={unlock} className="bg-white rounded-lg border border-zinc-200 p-6 w-full max-w-sm space-y-3"
          data-testid="public-gantt-password">
          <p className="text-sm font-semibold text-zinc-800">パスワードを入力してください</p>
          <input type="password" autoFocus value={password} onChange={(ev) => setPassword(ev.target.value)}
            data-testid="public-gantt-password-input" placeholder="パスワード / Password"
            className="w-full border border-zinc-300 rounded px-3 py-2 text-sm" />
          {pwError && <p className="text-xs text-rose-600" data-testid="public-gantt-password-error">{pwError}</p>}
          <button type="submit" disabled={!password} data-testid="public-gantt-password-submit"
            className="w-full py-2 rounded bg-teal-700 text-white text-sm font-semibold disabled:opacity-40">開く / Open</button>
        </form>
      </div>
    );
  }

  if (err) {
    return (
      <div className="min-h-screen bg-zinc-50 flex items-center justify-center p-6">
        <div className="bg-white rounded-lg border border-zinc-200 shadow-sm p-8 text-center max-w-sm" data-testid="public-gantt-error">
          <WarningCircle size={40} weight="duotone" className="text-rose-400 mx-auto mb-2" />
          <p className="text-sm text-zinc-700 font-medium">{err}</p>
          <p className="text-xs text-zinc-400 mt-1">共有が停止されたか、リンクが間違っています。</p>
        </div>
      </div>
    );
  }
  if (!data) {
    return <div className="min-h-screen bg-zinc-50 flex items-center justify-center text-sm text-zinc-400" data-testid="public-gantt-loading">読込中…</div>;
  }

  const items = [...(data.items || [])]
    .sort(ganttOrder((it) => it.end, (it) => it.start))
    .map((it) => ({ ...it, color: PRIORITY_BAR[it.priority] || '#4F46E5' }));
  // 空の列は表示しない（メンバー・備考が1件も無いときは列ごと省略）
  const isProject = data.kind === 'project';
  const showMembers = isProject && items.some((it) => (it.members || []).length > 0);
  const showNotes = isProject && items.some((it) => (it.notes || '').trim());
  // メンバーは先頭名のみ・同じ先頭名が複数人いる場合はフルネーム
  const displayName = makeNameShortener(items.flatMap((it) => it.members || []));

  return (
    <div className="min-h-screen bg-zinc-50" data-testid="public-gantt-page">
      <div className="max-w-6xl mx-auto p-4 sm:p-8 space-y-5">
        <div className="flex flex-wrap items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-indigo-600 text-white flex items-center justify-center shrink-0">
            <Kanban size={22} weight="duotone" />
          </div>
          <div className="min-w-0">
            <h1 className="text-lg sm:text-xl font-bold tracking-tight text-zinc-900 truncate" data-testid="public-gantt-title">{data.title}</h1>
            <p className="text-xs text-zinc-500">
              {data.kind === 'project' ? 'プロジェクト ガントチャート' : '部署 ガントチャート'}
              {data.start_date && ` · ${fmtDateFull(data.start_date)} 〜 ${fmtDateFull(data.end_date)}`}
              <span className="ml-2 text-zinc-400">閲覧専用</span>
            </p>
          </div>
        </div>
        <div className="bg-white rounded-xl border border-zinc-200 shadow-sm p-2 sm:p-4">
          <GanttChart items={items} emptyText="表示するデータがありません。" />
        </div>

        {/* 一覧 — 印刷フォームと同じ内容をWeb上でも確認できる */}
        {items.length > 0 && (
          <div className="bg-white rounded-xl border border-zinc-200 shadow-sm overflow-hidden" data-testid="public-gantt-list">
            <div className="flex items-center gap-1.5 px-4 py-2.5 border-b border-zinc-200">
              <ListChecks size={16} weight="duotone" className="text-indigo-500" />
              <h2 className="text-sm font-bold text-zinc-700">一覧</h2>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="text-xs text-zinc-500 bg-zinc-50/60 border-b border-zinc-200">
                  <tr>
                    <th className="text-left py-2 px-3">{isProject ? 'タスク' : 'プロジェクト'}</th>
                    <th className="text-left py-2 px-3">状態</th>
                    <th className="text-left py-2 px-3">優先度</th>
                    {showMembers && <th className="text-left py-2 px-3">メンバー</th>}
                    <th className="text-left py-2 px-3 whitespace-nowrap">開始〜期限</th>
                    {showNotes && <th className="text-left py-2 px-3">備考</th>}
                    {!isProject && <th className="text-left py-2 px-3">進捗</th>}
                  </tr>
                </thead>
                <tbody>
                  {items.map((it) => (
                    <tr key={it.id} className="border-b border-zinc-200 last:border-0" data-testid={`public-list-row-${it.id}`}>
                      <td className="py-2 px-3 font-medium text-zinc-800">{it.label}</td>
                      <td className="py-2 px-3">
                        {isProject ? (
                          <span className="text-xs text-zinc-600">{t(`planner.taskStatus.${it.status}`)}</span>
                        ) : (
                          <Badge variant="outline" data-testid={`public-project-status-${it.id}`}
                            className={`${PROJECT_STATUS_BADGE[projectBadgeKey(it)] || ''} text-[10px] px-1.5 py-0`}>
                            {t(`planner.projectStatus.${projectBadgeKey(it)}`)}
                          </Badge>
                        )}
                      </td>
                      <td className="py-2 px-3">
                        {it.priority ? (
                          <Badge variant="outline" className={`${PRIORITY_BADGE[it.priority] || ''} text-[10px] px-1.5 py-0`}>
                            {t(`planner.priority.${it.priority}`)}
                          </Badge>
                        ) : <span className="text-zinc-300">—</span>}
                      </td>
                      {showMembers && (
                        <td className="py-2 px-3 text-xs text-zinc-600">{(it.members || []).map(displayName).join('、') || '—'}</td>
                      )}
                      <td className="py-2 px-3 text-xs text-zinc-500 whitespace-nowrap">{oneOrRange(it.start, it.end)}</td>
                      {showNotes && (
                        <td className="py-2 px-3 text-xs text-zinc-600 whitespace-pre-wrap break-words max-w-64">{it.notes || '—'}</td>
                      )}
                      {!isProject && <td className="py-2 px-3 text-xs text-zinc-500">{it.sub || '—'}</td>}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        <div className="flex items-center gap-2 flex-wrap text-[11px] text-zinc-400">
          優先度:
          {['urgent', 'high', 'medium', 'low'].map((p) => (
            <Badge key={p} variant="outline" className={`${PRIORITY_BADGE[p]} text-[10px] px-1.5 py-0`}>
              {{ urgent: '緊急', high: '高', medium: '中', low: '低' }[p]}
            </Badge>
          ))}
        </div>
      </div>
    </div>
  );
}
